import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) return toast.error('Please complete the human-verification check');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email, turnstile_token: turnstileToken });
      setSent(true);
      toast.success(data.message || 'If that email is on file, we\'ve sent a reset link.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-8" data-testid="forgot-back-login">
          <ArrowLeft className="w-4 h-4" /> Back to log in
        </Link>
        <div className="flex items-center gap-2 mb-6">
          <img src="/brand/logo-icon@2x.png" alt="PTT NZ" className="w-10 h-10 rounded-lg" />
          <span className="font-display font-black text-xl">PTT NZ Alerts</span>
        </div>
        <h1 className="font-display font-black text-3xl mb-2">Forgot your password?</h1>
        <p className="text-zinc-600 mb-8">Enter the email on your account and we'll send you a link to reset your password.</p>

        {sent ? (
          <div className="card bg-teal-50 border-teal-200">
            <div className="text-teal-900">
              <div className="font-display font-bold text-lg mb-2">Check your inbox</div>
              <p className="text-sm">If <strong>{email}</strong> is on file, we've sent a password reset link there. It's valid for 1 hour.</p>
              <p className="text-sm mt-3">Didn't receive it? Check spam, or try again in a minute.</p>
            </div>
            <div className="mt-4 flex gap-3">
              <button onClick={() => { setSent(false); setEmail(''); }} className="btn btn-ghost" data-testid="forgot-try-again">Try another email</button>
              <button onClick={() => nav('/login')} className="btn btn-primary">Back to log in</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
            </div>
            <TurnstileWidget onVerify={setTurnstileToken} className="pt-1" />
            <button type="submit" className="btn btn-primary w-full" disabled={loading || !turnstileToken} data-testid="forgot-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
